import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Assessment,
  PlayArrow,
  Refresh,
} from '@mui/icons-material';

interface SimulationResult {
  scenario: string;
  revenue: number;
  expenses: number;
  profit: number;
  margin: number;
  breakEvenMonths: number;
  roi: number;
}

const BusinessPlanSimulator: React.FC = () => {
  // Input state
  const [initialInvestment, setInitialInvestment] = useState<number>(100000);
  const [monthlyRevenue, setMonthlyRevenue] = useState<number>(25000);
  const [monthlyExpenses, setMonthlyExpenses] = useState<number>(18000);
  const [growthRate, setGrowthRate] = useState<number>(5);
  const [timeframe, setTimeframe] = useState<number>(12);
  const [industry, setIndustry] = useState<string>('technology');
  
  // Results state
  const [results, setResults] = useState<SimulationResult[]>([]);
  const [showResults, setShowResults] = useState(false);

  const calculateScenarios = () => {
    const scenarios: SimulationResult[] = [];
    
    // Best Case (+20% revenue, -10% expenses)
    const bestRevenue = monthlyRevenue * 1.2 * timeframe;
    const bestExpenses = monthlyExpenses * 0.9 * timeframe;
    const bestProfit = bestRevenue - bestExpenses;
    const bestMargin = (bestProfit / bestRevenue) * 100;
    const bestBreakEven = initialInvestment / (monthlyRevenue * 1.2 - monthlyExpenses * 0.9);
    const bestROI = ((bestProfit - initialInvestment) / initialInvestment) * 100;
    
    scenarios.push({
      scenario: 'Best Case',
      revenue: bestRevenue,
      expenses: bestExpenses,
      profit: bestProfit,
      margin: bestMargin,
      breakEvenMonths: bestBreakEven,
      roi: bestROI,
    });
    
    // Realistic Case (as entered)
    const realisticRevenue = monthlyRevenue * timeframe;
    const realisticExpenses = monthlyExpenses * timeframe;
    const realisticProfit = realisticRevenue - realisticExpenses;
    const realisticMargin = (realisticProfit / realisticRevenue) * 100;
    const realisticBreakEven = initialInvestment / (monthlyRevenue - monthlyExpenses);
    const realisticROI = ((realisticProfit - initialInvestment) / initialInvestment) * 100;
    
    scenarios.push({
      scenario: 'Realistic',
      revenue: realisticRevenue,
      expenses: realisticExpenses,
      profit: realisticProfit,
      margin: realisticMargin,
      breakEvenMonths: realisticBreakEven,
      roi: realisticROI,
    });
    
    // Worst Case (-20% revenue, +10% expenses)
    const worstRevenue = monthlyRevenue * 0.8 * timeframe;
    const worstExpenses = monthlyExpenses * 1.1 * timeframe;
    const worstProfit = worstRevenue - worstExpenses;
    const worstMargin = (worstProfit / worstRevenue) * 100;
    const worstBreakEven = initialInvestment / (monthlyRevenue * 0.8 - monthlyExpenses * 1.1);
    const worstROI = ((worstProfit - initialInvestment) / initialInvestment) * 100;
    
    scenarios.push({
      scenario: 'Worst Case',
      revenue: worstRevenue,
      expenses: worstExpenses,
      profit: worstProfit,
      margin: worstMargin,
      breakEvenMonths: worstBreakEven,
      roi: worstROI,
    });
    
    setResults(scenarios);
    setShowResults(true);
  };

  const resetSimulation = () => {
    setInitialInvestment(100000);
    setMonthlyRevenue(25000);
    setMonthlyExpenses(18000);
    setGrowthRate(5);
    setTimeframe(12);
    setIndustry('technology');
    setShowResults(false);
    setResults([]);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getScenarioColor = (scenario: string) => {
    switch (scenario) {
      case 'Best Case':
        return 'success';
      case 'Realistic':
        return 'primary';
      case 'Worst Case':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Assessment fontSize="large" color="primary" />
            <Typography variant="h5" component="h2">
              Business Plan Financial Simulator
            </Typography>
          </Box>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            Test different financial scenarios to understand your business's potential. 
            Adjust the parameters below and run the simulation to see Best Case, Realistic, and Worst Case outcomes.
          </Alert>

          <Grid container spacing={3}>
            {/* Initial Investment */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Initial Investment"
                type="number"
                value={initialInvestment}
                onChange={(e) => setInitialInvestment(Number(e.target.value))}
                InputProps={{
                  startAdornment: <AttachMoney />,
                }}
                helperText="Total startup capital required"
              />
            </Grid>

            {/* Monthly Revenue */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Monthly Revenue (Target)"
                type="number"
                value={monthlyRevenue}
                onChange={(e) => setMonthlyRevenue(Number(e.target.value))}
                InputProps={{
                  startAdornment: <TrendingUp />,
                }}
                helperText="Expected monthly revenue"
              />
            </Grid>

            {/* Monthly Expenses */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Monthly Expenses"
                type="number"
                value={monthlyExpenses}
                onChange={(e) => setMonthlyExpenses(Number(e.target.value))}
                InputProps={{
                  startAdornment: <TrendingDown />,
                }}
                helperText="Total monthly operating costs"
              />
            </Grid>

            {/* Industry */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Industry</InputLabel>
                <Select
                  value={industry}
                  label="Industry"
                  onChange={(e) => setIndustry(e.target.value)}
                >
                  <MenuItem value="technology">Technology</MenuItem>
                  <MenuItem value="retail">Retail</MenuItem>
                  <MenuItem value="services">Services</MenuItem>
                  <MenuItem value="manufacturing">Manufacturing</MenuItem>
                  <MenuItem value="healthcare">Healthcare</MenuItem>
                  <MenuItem value="finance">Finance</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Growth Rate Slider */}
            <Grid item xs={12}>
              <Typography gutterBottom>
                Expected Monthly Growth Rate: {growthRate}%
              </Typography>
              <Slider
                value={growthRate}
                onChange={(_, value) => setGrowthRate(value as number)}
                min={0}
                max={20}
                step={1}
                marks
                valueLabelDisplay="auto"
              />
            </Grid>

            {/* Timeframe Slider */}
            <Grid item xs={12}>
              <Typography gutterBottom>
                Simulation Timeframe: {timeframe} months
              </Typography>
              <Slider
                value={timeframe}
                onChange={(_, value) => setTimeframe(value as number)}
                min={6}
                max={36}
                step={6}
                marks={[
                  { value: 6, label: '6mo' },
                  { value: 12, label: '1yr' },
                  { value: 24, label: '2yr' },
                  { value: 36, label: '3yr' },
                ]}
                valueLabelDisplay="auto"
              />
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PlayArrow />}
                  onClick={calculateScenarios}
                >
                  Run Simulation
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Refresh />}
                  onClick={resetSimulation}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Results Section */}
          {showResults && results.length > 0 && (
            <Box mt={4}>
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="h6" gutterBottom>
                Simulation Results
              </Typography>

              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {results.map((result) => (
                  <Grid item xs={12} md={4} key={result.scenario}>
                    <Paper elevation={2} sx={{ p: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Chip
                          label={result.scenario}
                          color={getScenarioColor(result.scenario) as any}
                          size="small"
                        />
                        {result.profit > 0 ? (
                          <TrendingUp color="success" />
                        ) : (
                          <TrendingDown color="error" />
                        )}
                      </Box>
                      
                      <Typography variant="h4" color={result.profit > 0 ? 'success.main' : 'error.main'}>
                        {formatCurrency(result.profit)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Projected Profit
                      </Typography>
                      
                      <Box mt={2}>
                        <Typography variant="body2">
                          Revenue: {formatCurrency(result.revenue)}
                        </Typography>
                        <Typography variant="body2">
                          Expenses: {formatCurrency(result.expenses)}
                        </Typography>
                        <Typography variant="body2">
                          Margin: {result.margin.toFixed(1)}%
                        </Typography>
                        <Typography variant="body2">
                          ROI: {result.roi.toFixed(1)}%
                        </Typography>
                        <Typography variant="body2">
                          Break-even: {result.breakEvenMonths > 0 && isFinite(result.breakEvenMonths) 
                            ? `${result.breakEvenMonths.toFixed(1)} months`
                            : 'Not achievable'}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* Detailed Table */}
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Scenario</strong></TableCell>
                      <TableCell align="right"><strong>Revenue</strong></TableCell>
                      <TableCell align="right"><strong>Expenses</strong></TableCell>
                      <TableCell align="right"><strong>Profit</strong></TableCell>
                      <TableCell align="right"><strong>Margin %</strong></TableCell>
                      <TableCell align="right"><strong>ROI %</strong></TableCell>
                      <TableCell align="right"><strong>Break-even</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map((result) => (
                      <TableRow key={result.scenario}>
                        <TableCell>
                          <Chip
                            label={result.scenario}
                            color={getScenarioColor(result.scenario) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">{formatCurrency(result.revenue)}</TableCell>
                        <TableCell align="right">{formatCurrency(result.expenses)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: result.profit > 0 ? 'success.main' : 'error.main' }}>
                          {formatCurrency(result.profit)}
                        </TableCell>
                        <TableCell align="right">{result.margin.toFixed(1)}%</TableCell>
                        <TableCell align="right">{result.roi.toFixed(1)}%</TableCell>
                        <TableCell align="right">
                          {result.breakEvenMonths > 0 && isFinite(result.breakEvenMonths)
                            ? `${result.breakEvenMonths.toFixed(1)} mo`
                            : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Alert severity="warning" sx={{ mt: 2 }}>
                <strong>Note:</strong> These are projections based on your inputs. Actual results may vary. 
                Use these scenarios for planning purposes and always consult with financial advisors for major business decisions.
              </Alert>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BusinessPlanSimulator;
